import { useCallback, useEffect, useState } from "react";
import { Plus, BellRing } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useCollection } from "@/hooks/useCollection";
import { PRIORITY } from "@/constants/crm";
import { PageHeader, Panel, StatCard, PillTabs, Empty, NativeSelect } from "@/components/crm/common";
import { FormDialog } from "@/components/crm/FormDialog";
import { TaskCard } from "@/components/crm/artist/TaskCard";

const COLUMNS = ["Pending", "In Progress", "HOD Review", "Changes Requested", "Complete"];
const MSG = { start: "Work started", progress: "Progress updated", submit: "Sent to HOD for approval", review: "Review saved" };

function useBoard() {
  const [board, setBoard] = useState(null);
  const load = useCallback(() => api.get("/artist/board").then((r) => setBoard(r.data)).catch(() => {}), []);
  useEffect(() => {
    load();
    const t = setInterval(() => { if (!document.hidden) load(); }, 10000);
    return () => clearInterval(t);
  }, [load]);
  return [board, load];
}

function Grid({ rows, mode, act, onChanges, empty }) {
  if (!rows.length) return <Panel><Empty text={empty} /></Panel>;
  return <div className="grid gap-4 md:grid-cols-2 2xl:grid-cols-3">{rows.map((t) => <TaskCard key={t.id} t={t} mode={mode} act={act} onChanges={onChanges} />)}</div>;
}

function Kanban({ rows }) {
  return (
    <div className="thin-scroll -mx-1 flex gap-3 overflow-x-auto px-1 pb-2" data-testid="artist-kanban">
      {COLUMNS.map((c) => {
        const col = rows.filter((t) => t.status === c || (c === "Pending" && t.status === "Delayed"));
        return (
          <div key={c} data-testid={`artist-col-${c.replace(/\s/g, "-").toLowerCase()}`} className="w-72 shrink-0 rounded-xl bg-zinc-50 p-2">
            <div className="flex items-center justify-between px-1.5 py-1 text-xs font-semibold uppercase tracking-wider text-zinc-500">{c}<span className="rounded-full bg-white px-2 py-0.5 text-zinc-700">{col.length}</span></div>
            <div className="mt-1 space-y-2">{col.map((t) => <TaskCard key={t.id} t={t} mode="view" />)}</div>
          </div>
        );
      })}
    </div>
  );
}

export default function ArtistWork() {
  const [board, load] = useBoard();
  const { items: projects } = useCollection("projects");
  const [tab, setTab] = useState("mine");
  const [assign, setAssign] = useState(null);
  const [changes, setChanges] = useState(null);
  const [dept, setDept] = useState("");
  if (!board) return <PageHeader num="22" title="Artist Work" subtitle="Loading…" />;
  const me = board.me;
  const tasks = board.tasks.filter((t) => t.kind !== "content" || t.status === "HOD Review");
  const mine = tasks.filter((t) => me && (t.assignee_member_id === me.id || t.assignee === me.name));
  const team = board.is_admin ? tasks : tasks.filter((t) => board.team_names.includes(t.assignee) || t.lead_user_id === me?.user_id);
  const reviews = tasks.filter((t) => t.status === "HOD Review" && (board.is_admin || (me && (t.hod_names || []).includes(me.name))));
  const act = async (t, action, body) => {
    try {
      await api.post(`/artist/tasks/${t.id}/${action}`, body || {});
      toast.success(MSG[action]);
      await load();
      return true;
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); return false; }
  };
  const tabs = [["mine", "My work", mine.filter((t) => t.status !== "Complete").length], ...(board.can_assign ? [["team", "My team", team.length]] : []),
    ["hod", "HOD approvals", reviews.length], ["board", "Department board"]];
  const depts = [...new Set(tasks.map((t) => t.department).filter(Boolean))];
  return (
    <>
      <PageHeader num="22" title="Artist Work" subtitle="Leads assign work to artists, artists update progress and upload finals, HODs approve. Every step updates Work Assignment and Approvals & QC automatically.">
        {board.is_admin && <Button data-testid="artist-overdue-run" variant="outline" onClick={async () => {
          try { const { data } = await api.post("/artist/overdue-alerts/run"); toast.success(`${data.alerted.length} overdue reminder(s) sent${data.skipped.length ? ` · ${data.skipped.length} skipped (no login)` : ""}`); await load(); }
          catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
        }}><BellRing className="mr-1 h-4 w-4" />Send overdue reminders</Button>}
        {board.can_assign && <Button data-testid="artist-assign-button" onClick={() => setAssign({ priority: "Normal" })}><Plus className="mr-1 h-4 w-4" />Assign work</Button>}
      </PageHeader>
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <StatCard testid="artist-stat-open" label="My open tasks" value={mine.filter((t) => t.status !== "Complete").length} hint={me ? `${me.name} · ${me.department}` : "Not linked to a team member"} />
        <StatCard testid="artist-stat-progress" label="In progress" value={mine.filter((t) => t.status === "In Progress").length} tone="text-[#A9792C]" />
        <StatCard testid="artist-stat-review" label="Awaiting my HOD review" value={reviews.length} tone="text-amber-600" />
        <StatCard testid="artist-stat-changes" label="Changes requested" value={mine.filter((t) => t.status === "Changes Requested").length} tone="text-rose-600" />
      </div>
      <PillTabs testid="artist-tab" value={tab} onChange={setTab} tabs={tabs} />
      {tab === "mine" && <Grid rows={mine} mode="artist" act={act} empty={me ? "No work assigned to you yet." : "Your login is not linked to a team member yet. Ask the admin to import the team roster."} />}
      {tab === "team" && <Grid rows={team} mode={board.is_admin ? "artist" : "view"} act={act} empty="No work assigned to your team yet. Click Assign work." />}
      {tab === "hod" && <Grid rows={reviews} mode="hod" act={act} onChanges={setChanges} empty="Nothing waiting for your approval." />}
      {tab === "board" && (
        <>
          <NativeSelect testid="artist-dept-filter" value={dept} onChange={setDept} placeholder="All departments" options={depts} />
          <Kanban rows={dept ? tasks.filter((t) => t.department === dept) : tasks} />
        </>
      )}
      <FormDialog testid="artist-assign-form" open={!!assign} onOpenChange={(o) => !o && setAssign(null)} title="Assign work to an artist" initial={assign}
        fields={[{ name: "project_id", label: "Project", type: "select", options: projects.map((p) => ({ value: p.id, label: p.name })), required: true },
          { name: "assignee_id", label: "Artist", type: "select", options: board.assignable.map((a) => ({ value: a.id, label: `${a.name} · ${a.title || a.department}` })), required: true },
          { name: "deliverable", label: "Deliverable", required: true, full: true }, { name: "deadline", label: "Deadline", type: "date", required: true },
          { name: "priority", label: "Priority", type: "select", options: PRIORITY }, { name: "brief", label: "Brief / reference links", type: "textarea" }]}
        onSubmit={async (f) => {
          try { await api.post("/artist/tasks", f); toast.success("Work assigned. The artist has been notified in chat."); await load(); return true; }
          catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); return false; }
        }} />
      <FormDialog testid="artist-changes-form" open={!!changes} onOpenChange={(o) => !o && setChanges(null)} title="Request changes" submitLabel="Send back to artist" initial={{}}
        fields={[{ name: "comment", label: "What needs to change?", type: "textarea", required: true, full: true }]}
        onSubmit={async (f) => act(changes, "review", { decision: "changes", comment: f.comment })} />
    </>
  );
}
